import { FC, Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';

import { MentorMessage, RealityCheck, TimeLimit } from '~/components/account/async';
import { SEON } from '~/components/authentication/async';
import { selectShowMentorMessageDialog } from '~/components/account/ducks';
import { selectLossLimitMessage } from '~/components/account/limits/ducks/selectors';
import { selectVerificationModalIsOpen } from '~/components/account/phone-verification-modal/phone-verification-modal-duck';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { selectOptInMessageOpen } from '~/components/bonus/ducks/selectors';
import {
	selectDisableProposedLocaleForFranchise,
	selectIsBotRequest,
	selectProposedLocale,
	selectShowMentorMessages,
	selectIsSeonEnabled,
	selectIsSeonServiceEnabled,
	selectLicense
} from '~/components/brand/ducks/selectors';
import InvalidLocaleReload from '~/components/invalid-locale-reload';
import { ProposeLocale } from '~/components/locale/async';
import useConfiguration from '~/configuration/use-configuration';
import { isNativeMentorMessageEnabled, isNativeTimeLimitEnabled } from '~/components/app-bridge/app-properties';
import { LICENSE_KSA } from '~/utils/license-utils';
import usePrevious from '~/hooks/use-previous';
/**
 * Components lazy loaded
 */
const LossLimitModal = lazy(() => import('~/components/limits/loss-limit-modal'));
const PhoneVerificationModal = lazy(
	() => import('~/components/account/phone-verification-modal/phone-verification-modal-container')
);
const OneClickOptInMessage = lazy(() => import('~/components/bonus/bonus-opt-in-message'));

const RegulationsCommon: FC = () => {
	const showLossLimitMessage = useSelector(selectLossLimitMessage);
	const authenticated = useSelector(authenticationSelector);
	const verificationModalIsOpen = useSelector(selectVerificationModalIsOpen);
	const showMentorMessages = useSelector(selectShowMentorMessages);
	const showMentorMessageDialog = useSelector(selectShowMentorMessageDialog);
	const oneClickOptInMessageOpen = useSelector(selectOptInMessageOpen);
	const proposedLocale = useSelector(selectProposedLocale);
	const { isIntegrationTesting } = useConfiguration();
	const isBotRequest = useSelector(selectIsBotRequest);
	const isSeonServiceEnabled = useSelector(selectIsSeonServiceEnabled);
	const isSeonDeviceIdEnabled = useSelector(selectIsSeonEnabled);
	const license = useSelector(selectLicense);
	const prevAuthenticated = usePrevious(authenticated);
	const sessionActivated =
		typeof prevAuthenticated !== 'undefined' && prevAuthenticated !== authenticated && authenticated;
	// hacky temp solution for request PYRO-565
	const disableProposedLocale = useSelector(selectDisableProposedLocaleForFranchise) && authenticated;
	const hideProposedLocale = isIntegrationTesting || isBotRequest || disableProposedLocale;
	const mentorMessageEnabled = showMentorMessages && showMentorMessageDialog && !isNativeMentorMessageEnabled();
	const timeLimitEnabled = authenticated && license !== LICENSE_KSA && !isNativeTimeLimitEnabled();

	return (
		<Suspense fallback={<div className="spinner" />}>
			{showLossLimitMessage && <LossLimitModal />}
			{/* for KSA we use TimeLimitKSA in regulations-container-ksa.tsx */}
			{timeLimitEnabled && <TimeLimit />}
			{verificationModalIsOpen && <PhoneVerificationModal />}
			{mentorMessageEnabled && <MentorMessage />}
			{oneClickOptInMessageOpen && <OneClickOptInMessage />}
			{proposedLocale && !hideProposedLocale && <ProposeLocale />}
			<RealityCheck />
			<InvalidLocaleReload />
			{isSeonServiceEnabled && isSeonDeviceIdEnabled && sessionActivated && <SEON />}
		</Suspense>
	);
};

export default RegulationsCommon;
